import { camelCase } from 'lodash'
import moment from 'moment'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { useNavigate } from 'react-router'
import { modifyPage, modifySubLayout } from '../articleAPI'
import { copyPage } from '../../page/pageAPI'
import { copySubLayout } from '../../subLayout/subLayoutAPI'
import { getHandleEditPageSubmit, PageUpsertForm, PageCopyForm, StartLayoutForm } from '../../page'
import { getHandleEditSubLayoutSubmit, SubLayoutUpsertForm, SubLayoutCopyForm } from '../../subLayout'
import { openModal, closeModal } from '../articleActions'
import { isLayoutEngineExecutableStatus, isLayoutViewableStatus } from '../ArticleParse'
import { getLayoutableName } from './utils'
import type { FormConfig } from '../../common/form/CustomForm'
import type { Layoutable } from '../types'
import type { StatusValue } from '../../common/types'
import type { Setting } from '../../media'

export type LayoutableWithMeta = Layoutable & {
  status?: StatusValue
  assignee?: { id?: number | string }
  name?: string
  layoutResult?: unknown
  layoutIndex?: number
  pageDrafts?: { draftNumber: number }[]
  subLayoutDrafts?: { draftNumber: number }[]
  [key: string]: unknown
}

type PageWithMeta = LayoutableWithMeta & {
  id: number
  name: string
  issueDate: string
  draftNumber: number
  media: { id: number | string }
  setting: { id: number | string }
  assignee?: { id?: number | string }
  pageDrafts: { draftNumber: number }[]
}

type SubLayoutWithMeta = LayoutableWithMeta & {
  id: number
  name: string
  issueDate: string
  draftNumber: number
  linePlacement: string
  borderLineWidth?: string
  media: { id: number | string }
  setting: { id: number | string }
  assignee?: { id?: number | string }
  subLayoutDrafts: { draftNumber: number }[]
}

export interface ButtonConfig {
  handleClick: () => void
  disabled?: boolean
  hidden?: boolean
  icon: string
  variant?: string
  text: string
}

export interface ModalSetting {
  name: string
  button: ButtonConfig
  form?: FormConfig
}

export const isLayoutShowable = (layoutable: LayoutableWithMeta | null) =>
  layoutable?.status ? isLayoutViewableStatus(layoutable.status) : false

export const getHeaderModalSettings = ({
  dispatch,
  navigate,
  layoutable,
  layoutableType,
  layoutableId,
  layoutableBaseUrl,
  mediaList,
  settingList,
  userList,
  fetching,
  enumValues,
  currentUser,
  closeAlert,
  isSelectedLayoutableReadable,
  isSelectedLayoutableWritable,
  handleStartLayoutSubmit,
  alert
}: {
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
  navigate: ReturnType<typeof useNavigate>
  layoutable: LayoutableWithMeta
  layoutableType: string
  layoutableId: number
  layoutableBaseUrl: string
  mediaList: unknown
  settingList: Setting[]
  userList: unknown
  fetching: boolean
  enumValues: unknown
  currentUser: unknown
  closeAlert: () => void
  isSelectedLayoutableReadable: boolean
  isSelectedLayoutableWritable: boolean
  handleStartLayoutSubmit: (values: { quickLayout: boolean }) => void
  alert: unknown
}): ModalSetting[] => {
  let layoutableEditSetting: ModalSetting | undefined
  let layoutableCopySetting: ModalSetting | undefined
  const layoutableName = getLayoutableName(layoutableType)
  switch (layoutableType) {
    case 'Page': {
      const page = layoutable as PageWithMeta
      const handleEditSubmit = getHandleEditPageSubmit(dispatch, modifyPage)
      const handleCopySubmit = ({ draftNumber, copyLayoutInfo }: { draftNumber: number; copyLayoutInfo: boolean }) => {
        dispatch(copyPage({ id: page.id, draftNumber, copyLayoutInfo, navigate }))
        dispatch(closeModal())
      }
      layoutableEditSetting = {
        name: 'page-edit',
        button: {
          handleClick: () => dispatch(openModal('page-edit')),
          disabled: fetching,
          icon: 'pencil',
          text: `${layoutableName}設定変更`
        },
        form: {
          initialValues: () => {
            if (!page) return {}
            const issueDate = moment(page.issueDate).format('YYYY-MM-DD')
            const mediaId = page.media.id
            const selectedSettingList = mediaId !== '' ? settingList?.filter((s) => s.media.id === mediaId) : []
            const { name: _name, setting: _setting, media: _media, assignee: _assignee, ...pageProps } = page
            return {
              ...pageProps,
              layoutableType,
              layoutableId,
              mediaId,
              settingId: page.setting.id,
              assigneeId: page?.assignee?.id,
              selectedSettingList,
              issueDate,
              templateFile: ''
            }
          },
          handleSubmit: handleEditSubmit,
          validationSchema: PageUpsertForm.schema,
          Component: PageUpsertForm,
          props: {
            title: `${layoutableName}編集`,
            fetching,
            enumValues,
            onClickCancel: () => dispatch(closeModal()),
            submitButtonText: '変更する',
            mediaList,
            settingList,
            userList,
            currentUser
          }
        }
      }
      layoutableCopySetting = {
        name: 'copy',
        button: {
          handleClick: () => {
            closeAlert()
            dispatch(openModal('copy'))
          },
          disabled: fetching || !isSelectedLayoutableReadable,
          icon: 'copy',
          text: `${layoutableName}複製`
        },
        form: {
          initialValues: () => {
            if (!page) return {}
            const largestDraftNumber =
              page.pageDrafts
                .map((p: { draftNumber: number }) => p.draftNumber)
                .sort((a: number, b: number) => b - a)?.[0] ?? page.draftNumber
            return { name: page.name, draftNumber: largestDraftNumber + 1, copyLayoutInfo: false }
          },
          handleSubmit: handleCopySubmit,
          validationSchema: PageCopyForm.schema,
          Component: PageCopyForm,
          props: {
            title: `${layoutableName}複製`,
            fetching,
            onClickCancel: () => dispatch(closeModal()),
            submitButtonText: '複製する',
            alert
          }
        }
      }
      break
    }
    case 'SubLayout': {
      const subLayout = layoutable as SubLayoutWithMeta
      const handleEditSubmit = getHandleEditSubLayoutSubmit(dispatch, modifySubLayout)
      const handleCopySubmit = ({ draftNumber, copyLayoutInfo }: { draftNumber: number; copyLayoutInfo: boolean }) => {
        dispatch(copySubLayout({ id: subLayout.id, draftNumber, copyLayoutInfo, navigate }))
        dispatch(closeModal())
      }
      layoutableEditSetting = {
        name: 'subLayout-edit',
        button: {
          handleClick: () => dispatch(openModal('subLayout-edit')),
          disabled: fetching,
          icon: 'pencil',
          text: `${layoutableName}設定変更`
        },
        form: {
          initialValues: () => {
            if (!subLayout) return {}
            const issueDate = moment(subLayout.issueDate).format('YYYY-MM-DD')
            const mediaId = subLayout.media.id
            const selectedSettingList = settingList?.filter((s) => s.media.id === mediaId) || []
            const useBorderLine = subLayout.linePlacement !== 'l0'
            const { linePlacement, ...subLayoutProps } = subLayout
            return {
              ...subLayoutProps,
              layoutableType,
              layoutableId,
              subLayoutId: subLayout.id,
              mediaId,
              settingId: subLayout.setting.id,
              assigneeId: subLayout?.assignee?.id,
              selectedSettingList,
              useBorderLine,
              linePlacement: camelCase(linePlacement),
              borderLineWidth: subLayout?.borderLineWidth || '',
              issueDate
            }
          },
          handleSubmit: handleEditSubmit,
          validationSchema: SubLayoutUpsertForm.schema,
          Component: SubLayoutUpsertForm,
          props: {
            title: `${layoutableName}編集`,
            fetching,
            enumValues,
            onClickCancel: () => dispatch(closeModal()),
            submitButtonText: '変更する',
            mediaList,
            settingList,
            userList,
            currentUser
          }
        }
      }
      layoutableCopySetting = {
        name: 'subLayout-copy',
        button: {
          handleClick: () => {
            closeAlert()
            dispatch(openModal('subLayout-copy'))
          },
          disabled: fetching || !isSelectedLayoutableReadable,
          icon: 'copy',
          text: `${layoutableName}複製`
        },
        form: {
          initialValues: () => {
            if (!subLayout) return {}
            const largestDraftNumber =
              subLayout.subLayoutDrafts
                .map((p: { draftNumber: number }) => p.draftNumber)
                .sort((a: number, b: number) => b - a)?.[0] ?? subLayout.draftNumber
            return { name: subLayout.name, draftNumber: largestDraftNumber + 1, copyLayoutInfo: false }
          },
          handleSubmit: handleCopySubmit,
          validationSchema: SubLayoutCopyForm.schema,
          Component: SubLayoutCopyForm,
          props: {
            title: `${layoutableName}複製`,
            fetching,
            onClickCancel: () => dispatch(closeModal()),
            submitButtonText: '複製する',
            alert
          }
        }
      }
      break
    }
  }

  return [
    layoutableCopySetting,
    layoutableEditSetting,
    {
      name: 'show-layout',
      button: {
        handleClick: () => navigate(`/${layoutableBaseUrl}/layout`),
        icon: 'table',
        text: '紙面表示',
        disabled: !isLayoutShowable(layoutable)
      }
    },
    {
      name: 'start-layout',
      button: {
        handleClick: () => dispatch(openModal('start-layout')),
        icon: 'circle-play',
        text: '割り付け実行',
        disabled:
          fetching ||
          !isSelectedLayoutableWritable ||
          !(layoutable?.status && isLayoutEngineExecutableStatus(layoutable.status))
      },
      form: {
        initialValues: { quickLayout: true },
        handleSubmit: handleStartLayoutSubmit,
        Component: StartLayoutForm,
        props: { onClickCancel: () => dispatch(closeModal()), layoutableName: layoutable?.name, fetching }
      }
    }
  ].filter((s): s is ModalSetting => s !== undefined)
}
